import React, { useState } from 'react';
import { CaseStudyContent } from '../../types/case-study';
import LazyImage from '../lazy-image';
import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed';
import CtaWithArrow from '../cta-with-arrow';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'gatsby';
import mq from '../../utils/mq';

const CaseStudySquareCardOuterContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ImageContainer = styled.a`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  aspect-ratio: 1/1;
  overflow: hidden;
  cursor: pointer;
  border-radius: var(--Radius-Radius-3, 16px);
  ${mq.tablet(css`
    aspect-ratio: 16/9;
  `)};
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.1) 0%,
      rgba(0, 0, 0, 0.1) 100%
    ),
    linear-gradient(0deg, rgba(0, 0, 0, 0.45) 0%, rgba(0, 0, 0, 0.1) 100%);
  z-index: 1;
`;

const TitleContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  color: #fff;
  padding: 24px;
  z-index: 2;
  @media (max-width: 1050px) {
    padding: 16px;
  }
`;

const Title = styled.h3`
  color: #fff;
  font-size: 32px;
  font-style: normal;
  font-weight: 600;
  text-align: left;
  line-height: 36px;
  letter-spacing: 0;
  @media (max-width: 1050px) {
    font-size: 28px;
    line-height: 32px;
  }
`;

const LinkContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding-top: 28px;
  ${mq.mobile(css`
    padding-top: 16px;
  `)};
`;

export default function CaseStudySquareCard({
  full_slug,
  content
}: {
  full_slug: string;
  content: CaseStudyContent;
}) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <CaseStudySquareCardOuterContainer>
      <ImageContainer
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        href={`/${full_slug}`}
      >
        <TitleContainer>
          <Title>{content?.title}</Title>
        </TitleContainer>
        <GradientOverlay />
        <motion.div
          style={{ height: '100%', width: '100%', willChange: 'transform' }}
          animate={
            isHovered
              ? {
                  scale: 1.03,
                  transition: { duration: 0.6, ease: [0.26, 1, 0.5, 1] }
                }
              : {
                  scale: 1,
                  transition: { duration: 0.3, ease: [0.26, 1, 0.5, 1] }
                }
          }
        >
          <LazyImage
            fluid={
              content?.gridImage
                ? getFluidGatsbyImage(content?.gridImage)
                : undefined
            }
            width="100%"
            height="100%"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </motion.div>
      </ImageContainer>
      <LinkContainer>
        <CtaWithArrow
          label="View Case Study"
          darkTheme
          link={`/${full_slug}`}
          isHovered={isHovered}
        />
      </LinkContainer>
    </CaseStudySquareCardOuterContainer>
  );
}
